import { debounce } from 'lodash';
import React from 'react';
import { SortingRule } from '@components/table';
import { userSearchDebounce } from '../../components/hooks/use-search';
import { useWrapRequest } from 'use-wrap-request';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import {
    ActivityType,
    WorkoutCategory,
    WorkoutTypeV2
} from '../../core/api/FitnessActivityClient';
import { WorkoutRecordingMethod } from './recording-method.types';

export interface ActivityPointsRulesFilters {
    activityType?: ActivityType;
    workoutCategory?: WorkoutCategory;
    workoutType?: WorkoutTypeV2;
    recordingMethod?: WorkoutRecordingMethod;
    requiresAverageSpeed?: boolean;
    requiresCaloriesPerMinute?: boolean;
}

export function useActivityPointsRulesStore() {
    const { fitnessActivityClient } = injectTSDI(Api);
    const defaultGroupingSort =
        'activityType,workoutCategory,workoutType,-priority';

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 100,
        page: 0,
        sort: defaultGroupingSort
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    const [filters, setFiltersState] =
        React.useState<ActivityPointsRulesFilters>({});

    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const updateFilters = React.useCallback(
        (nextFilters: Partial<ActivityPointsRulesFilters>) => {
            setFiltersState((current) => ({ ...current, ...nextFilters }));
            dispatch({ type: 'page', page: 0 });
        },
        [dispatch]
    );

    const clearFilters = React.useCallback(() => {
        setFiltersState({});
        dispatch({ type: 'page', page: 0 });
    }, [dispatch]);

    const activityPointsRules = useWrapRequest(
        async (
            searchParams?: QueryParamsDto,
            filters?: ActivityPointsRulesFilters
        ) => {
            const query = {
                page: searchParams?.page || 0,
                size: searchParams?.size || 100,
                search: searchParams?.search,
                sort: searchParams?.sort,
                ...(filters?.activityType
                    ? { activityType: filters.activityType }
                    : {}),
                ...(filters?.workoutCategory
                    ? { workoutCategory: filters.workoutCategory }
                    : {}),
                ...(filters?.workoutType
                    ? { workoutType: filters.workoutType }
                    : {}),
                ...(filters?.recordingMethod
                    ? { recordingMethod: filters.recordingMethod }
                    : {}),
                ...(filters?.requiresAverageSpeed !== undefined
                    ? { requiresAverageSpeed: filters.requiresAverageSpeed }
                    : {}),
                ...(filters?.requiresCaloriesPerMinute !== undefined
                    ? {
                          requiresCaloriesPerMinute:
                              filters.requiresCaloriesPerMinute
                      }
                    : {})
            };

            const result =
                await fitnessActivityClient.admin.activityPointsMultiplierTierGroupControllerGetPage(
                    query,
                    {}
                );

            return {
                ...result.data,
                content: result.data.content || []
            };
        },
        {
            defaultData: {
                content: [],
                totalElements: 0,
                number: 0,
                size: 100,
                totalPages: 0
            }
        }
    );

    const debouncedAPICall = React.useCallback(
        debounce(
            (
                searchParams?: QueryParamsDto,
                filters?: ActivityPointsRulesFilters
            ) => {
                activityPointsRules.request([searchParams, filters]);
            },
            100
        ),
        [activityPointsRules]
    );

    React.useEffect(() => {
        debouncedAPICall(searchParams, filters);
    }, [searchParams, filters, debouncedAPICall]);

    return {
        activityPointsRules,
        searchParams,
        filters,
        sorting,
        setSorting,
        setSearchTerm,
        updateFilters,
        clearFilters,
        dispatch
    };
}
